import { useTenantId } from '@/stores/tenant.store';
import { notifyDispatchUpdate } from '@/hooks/scheduler/useDispatchQueueSync';
import {
  sendScheduleChangeNotification,
  type ScheduleNotificationChoice,
} from '@/hooks/jobs/useScheduleChangeNotification';
import type { CalendarArrivalWindowChange } from '@/lib/scheduler/calendarArrivalWindowChange';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiPost } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, jobKeys, schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerBooking } from '@/types/scheduler';

export interface MoveBookingDto {
  notification?: ScheduleNotificationChoice;
  sourceTechnicianId?: string;
  arrivalWindowChange?: CalendarArrivalWindowChange;
  technicianId?: string;
  startAt?: string;
  /** NOT-329 — explicit new end for a duration resize (omit to preserve duration). */
  endAt?: string;
  overrideConflicts?: boolean;
  manualScheduling?: boolean;
  restoreCancelled?: boolean;
  /** Suppress/allow the customer reschedule notice (defaults to the server rule). */
  notifyCustomer?: boolean;
  /**
   * Scheduling v2 (`/v2` facade only; v1 ignores it): what happens to the
   * customer promise when the plan moves. `KEEP` is the default and preserves
   * accepted availability. An outside-window move needs a customer change offer.
   */
  repromise?: 'MATCH' | 'KEEP';
}

/**
 * Dispatch-board drag-move: relocate an appointment to a new tech and/or time.
 * Errors (incl. 409 BLOCK-conflict) are surfaced to the caller — the board
 * reverts the drag and offers an admin override — so the auto-toast is off.
 */
export function useMoveBooking() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useApiMutation<SchedulerBooking, { id: string; dto: MoveBookingDto }>({
    mutationFn: async ({ id, dto }) => {
      const { notification, ...windowChange } = dto.arrivalWindowChange ?? {};
      const { notification: moveNotification, ...moveDto } = dto;
      const updated = await apiPost<SchedulerBooking, MoveBookingDto>(
        operationsClient,
        schedulerEndpoints.move(id),
        {
          ...moveDto,
          notifyCustomer: dto.notifyCustomer ?? false,
          ...(dto.arrivalWindowChange
            ? { arrivalWindowChange: windowChange as CalendarArrivalWindowChange }
            : {}),
        },
      );
      await sendScheduleChangeNotification(
        updated.appointmentId ?? id,
        notification ?? moveNotification,
        updated.arrivalWindow ??
          (updated.promise?.startAt && updated.promise.endAt
            ? { start: updated.promise.startAt, end: updated.promise.endAt }
            : null),
      );
      return updated;
    },
    showErrorToast: false,
    onSuccess: () => {
      notifyDispatchUpdate(tenantId);
      // Invalidate the whole scheduler namespace, not just `bookings()` (no
      // params): the calendar fetches under `bookings({ dateFrom, dateTo })`, and
      // the param-less key doesn't prefix-match that, so a move wouldn't refetch
      // it. Mirrors useScheduleBooking.
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      // The visit that moved belongs to a job, and the job's own views read it
      // through their own cache: without this the visit panel goes on showing
      // the time and arrival window the visit had before the move.
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
    },
  });
}
